// v2/components/admin/SiparisBildirimi.js — yeni siparis: kasa sesi + zil + liste + kart + sekme basligi
// AdminShell ust barinda durur; panelin her sayfasinda calisir.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bell, ShoppingCart, X, ArrowRight } from 'lucide-react';
import useYeniSiparis from '../../hooks/useYeniSiparis';
import { kasaSesiCal, sesiUyandir } from '../../utils/kasaSesi';
import { formatTRY } from '../../utils/format';
import b from './SiparisBildirimi.module.css';

const LISTE_MAX = 15;
const KART_MS = 12000;
const SES_SEVIYESI = 0.8;
export const YENI_SIPARIS_OLAYI = 'v2:yeni-siparis';

const saat = (iso) => (iso ? String(iso).slice(11, 16) : '');
const siparisYolu = (o) => `/v2/admin/orders?q=${encodeURIComponent((o.order_nos && o.order_nos[0]) || '')}`;
const numaralar = (o) => {
  const n = o.order_nos || [];
  return n.length > 1 ? `${n[0]} +${n.length - 1}` : (n[0] || '');
};

export default function SiparisBildirimi() {
  const konum = useLocation();
  const [liste, setListe] = useState([]);
  const [okunmamis, setOkunmamis] = useState(0);
  const [acik, setAcik] = useState(false);
  const [kart, setKart] = useState(null);
  const kartZamani = useRef(null);
  const kutu = useRef(null);

  // tarayici ses iznini ilk etkilesimde sessizce al
  useEffect(() => {
    const uyandir = () => {
      sesiUyandir();
      document.removeEventListener('pointerdown', uyandir);
      document.removeEventListener('keydown', uyandir);
    };
    document.addEventListener('pointerdown', uyandir);
    document.addEventListener('keydown', uyandir);
    return () => {
      document.removeEventListener('pointerdown', uyandir);
      document.removeEventListener('keydown', uyandir);
    };
  }, []);

  const yeniGeldi = useCallback((ogeler, sesCal) => {
    const yeniler = [...ogeler].reverse();           // en yeni en ustte
    setListe((l) => [...yeniler, ...l].slice(0, LISTE_MAX));
    setOkunmamis((n) => n + ogeler.length);
    setKart({ o: yeniler[0], ek: ogeler.length - 1, anahtar: Date.now() });
    if (sesCal) kasaSesiCal(SES_SEVIYESI);
    clearTimeout(kartZamani.current);
    kartZamani.current = setTimeout(() => setKart(null), KART_MS);
    try { window.dispatchEvent(new CustomEvent(YENI_SIPARIS_OLAYI)); } catch (e) { /* yok say */ }
  }, []);

  useYeniSiparis(true, yeniGeldi);
  useEffect(() => () => clearTimeout(kartZamani.current), []);

  // sekme basligi "(2) ..." — AdminShell sayfa degisince basligi yeniden yazar; ondan sonra calis
  useEffect(() => {
    const t = setTimeout(() => {
      const temiz = document.title.replace(/^\(\d+\)\s*/, '');
      document.title = okunmamis > 0 ? `(${okunmamis}) ${temiz}` : temiz;
    }, 0);
    return () => clearTimeout(t);
  }, [okunmamis, konum.pathname]);

  // liste disina tiklayinca / Esc ile kapat
  useEffect(() => {
    if (!acik) return undefined;
    const disari = (e) => { if (kutu.current && !kutu.current.contains(e.target)) setAcik(false); };
    const esc = (e) => { if (e.key === 'Escape') setAcik(false); };
    document.addEventListener('pointerdown', disari);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', disari);
      document.removeEventListener('keydown', esc);
    };
  }, [acik]);

  const ac = () => {
    setAcik((v) => !v);
    setOkunmamis(0);
  };

  return (
    <div className={b.wrap} ref={kutu}>
      <button
        type="button"
        className={`${b.bell} ${okunmamis ? b.bellOn : ''}`}
        onClick={ac}
        aria-expanded={acik}
        aria-label={okunmamis ? `${okunmamis} yeni sipariş` : 'Yeni siparişler'}
        title={okunmamis ? `${okunmamis} yeni sipariş` : 'Yeni siparişler'}
      >
        <Bell size={19} className={okunmamis ? b.ring : ''} />
        {okunmamis > 0 && <span className={b.badge}>{okunmamis > 99 ? '99+' : okunmamis}</span>}
      </button>

      {acik && (
        <div className={b.drop} role="dialog" aria-label="Yeni siparişler">
          <div className={b.dropHead}>
            <span className={b.dropTitle}>Yeni siparişler</span>
            <span className={b.dropSub}>Bu oturumda gelenler</span>
          </div>
          {liste.length === 0 ? (
            <div className={b.empty}>
              <Bell size={22} />
              <span>Yeni sipariş geldiğinde burada listelenir ve kasa sesi çalar.</span>
            </div>
          ) : (
            <ul className={b.list}>
              {liste.map((o, i) => (
                <li key={`${(o.order_nos || []).join('-')}-${i}`}>
                  <Link to={siparisYolu(o)} className={b.item} onClick={() => setAcik(false)}>
                    <span className={b.itemIcon}><ShoppingCart size={15} /></span>
                    <span className={b.itemBody}>
                      <span className={b.itemTitle} title={o.title}>{o.title}</span>
                      <span className={b.itemMeta}>{saat(o.time)} · {numaralar(o)} · {o.lines} kalem</span>
                    </span>
                    <span className={b.itemAmt}>{formatTRY(o.amount)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link to="/v2/admin/orders" className={b.all} onClick={() => setAcik(false)}>
            Tüm siparişler <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {kart && (
        <div key={kart.anahtar} className={b.toast} role="status" aria-live="polite">
          <span className={b.toastIcon}><ShoppingCart size={18} /></span>
          <Link
            to={siparisYolu(kart.o)} className={b.toastBody}
            onClick={() => { setKart(null); setOkunmamis(0); }}
          >
            <span className={b.toastTitle}>
              Yeni sipariş{kart.ek > 0 ? ` · +${kart.ek} sipariş daha` : ''}
            </span>
            <span className={b.toastText}>{kart.o.title}</span>
            <span className={b.toastMeta}>
              <b>{formatTRY(kart.o.amount)}</b> · {kart.o.lines} kalem · {saat(kart.o.time)} · KDV hariç
            </span>
          </Link>
          <button type="button" className={b.toastClose} onClick={() => setKart(null)} aria-label="Kapat">
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
